import React from "react";
import { NavLink, Outlet, Navigate, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Map, Users, AudioLines, BookUser, Calculator, ExternalLink, LogOut, Compass, House, FileText } from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", id: "admin-nav-dashboard" },
  { to: "/admin/home", icon: House, label: "Home & Sezioni", id: "admin-nav-home", adminOnly: true },
  { to: "/admin/pagine", icon: FileText, label: "Pagine & SEO", id: "admin-nav-pages", adminOnly: true },
  { to: "/admin/viaggi", icon: Map, label: "Spedizioni", id: "admin-nav-tours" },
  { to: "/admin/leader", icon: Users, label: "Tour Leader", id: "admin-nav-leaders" },
  { to: "/admin/media", icon: AudioLines, label: "Media CMS", id: "admin-nav-cms", adminOnly: true },
  { to: "/admin/crm", icon: BookUser, label: "CRM Clienti", id: "admin-nav-crm" },
  { to: "/admin/planning", icon: Calculator, label: "Planning & Markup", id: "admin-nav-planning", adminOnly: true },
];

export const AdminOnly = ({ children }) => {
  const { user, checking } = useAuth();
  if (checking) return null;
  return user && user.role === "admin" ? children : <Navigate to="/admin" replace />;
};

export default function AdminLayout() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();

  if (checking) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="font-mono text-xs tracking-[0.3em] text-copper animate-slowpulse">VERIFICA SESSIONE…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const isAdmin = user.role === "admin";
  const nav = NAV.filter((n) => isAdmin || !n.adminOnly);

  return (
    <div className="min-h-screen bg-obsidian text-cream" data-testid="admin-layout">
      <div className="flex">
        <aside className="hidden lg:flex flex-col w-64 fixed inset-y-0 border-r border-white/5 bg-obsidian2 p-6">
          <Link to="/admin" className="flex items-center gap-2.5 mb-10" data-testid="admin-logo">
            <Compass className="h-6 w-6 text-copper" />
            <span className="font-serif text-lg tracking-[0.16em] uppercase">De Los Cielos</span>
          </Link>
          <nav className="space-y-1 flex-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.id}
                className={({ isActive }) =>
                  "flex items-center gap-3 px-4 py-2.5 text-sm transition-colors " +
                  (isActive ? "bg-copper/15 text-copperlight border-l-2 border-copper" : "text-cream2 hover:text-cream border-l-2 border-transparent")
                }
              >
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="pt-6 border-t border-white/5 space-y-1">
            <Link to="/" className="flex items-center gap-3 px-4 py-2.5 text-sm text-cream2 hover:text-cream" data-testid="exit-admin-btn">
              <ExternalLink className="h-4 w-4" /> Torna al sito
            </Link>
            <button
              onClick={async () => {
                await logout();
                navigate("/admin/login");
              }}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-400/90 hover:text-red-300 w-full"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4" /> Esci
            </button>
            <div className="px-4 pt-3">
              <p className="font-mono text-[10px] text-sand">{user.email}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-copper mt-0.5">{user.role}</p>
            </div>
          </div>
        </aside>

        <div className="flex-1 lg:ml-64 min-w-0">
          <div className="lg:hidden sticky top-0 z-40 bg-obsidian/95 backdrop-blur border-b border-white/5">
            <div className="flex items-center justify-between px-4 h-14">
              <Link to="/admin" className="flex items-center gap-2">
                <Compass className="h-5 w-5 text-copper" />
                <span className="font-serif tracking-[0.14em] uppercase">Admin</span>
              </Link>
              <button onClick={logout} className="text-red-400" data-testid="admin-logout-btn-mobile"><LogOut className="h-5 w-5" /></button>
            </div>
            <div className="flex gap-1 px-3 pb-2 overflow-x-auto">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={n.id}
                  className={({ isActive }) =>
                    "shrink-0 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border " +
                    (isActive ? "border-copper text-copperlight" : "border-white/10 text-cream2")
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/" className="shrink-0 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border border-white/10 text-cream2" data-testid="exit-admin-btn">Sito</Link>
            </div>
          </div>
          <main className="p-4 sm:p-8 lg:p-10">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
